// src/components/Blog/LeftRail.jsx
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaLinkedin, FaFacebook, FaWhatsapp, FaTelegramPlane } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";



/* ---------- utils ---------- */
const slugify = (txt = "") =>
  txt.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");

const makeAbs = (p) => {
    if (!p) return "";
    try {
      // absolute URL already?
      return new URL(p).toString();
    } catch {
      // relative path -> resolve against current origin (only in browser)
      if (typeof window !== "undefined" && window.location?.origin) {
        return new URL(p, window.location.origin).toString();
      }
      return p; // SSR fallback
    }
  };

/* Fallback parse (H2 + H3) for when DOM isn't ready yet */
const parseMdH2H3 = (md = "") => {
  const lines = md.split(/\r?\n/);
  const items = [];
  for (const line of lines) {
    const m = /^(#{2,3})\s+(.+)$/.exec(line.trim());
    if (!m) continue;
    const level = m[1].length; // 2 or 3
    const text = m[2].replace(/[#*`_]+/g, "").trim();
    items.push({ id: slugify(text), text, level });
  }
  return items;
};

/* Read H2 + H3 from rendered DOM */
function useH2H3FromDom(containerRef) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const root = containerRef?.current;
    if (!root) return;

    const build = () => {
      const headings = Array.from(root.querySelectorAll("h2, h3"));
      const list = headings.map((el) => {
        const text = (el.innerText || el.textContent || "").trim();
        if (!el.id) el.id = slugify(text);
        return { id: el.id, text, level: Number(el.tagName.slice(1)) }; // 2 or 3
      });
      setItems(list);
    };

    build();
    const mo = new MutationObserver(build);
    mo.observe(root, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, [containerRef]);

  return items;
}

/* ---------- main ---------- */
export default function LeftRail({
  containerRef,
  title,
  markdown,            // optional raw MD for fallback
  headerOffset = 112,  // adjust to your sticky header height
  shareUrl,            // absolute or relative; falls back to current location
}) {
  const domItems = useH2H3FromDom(containerRef);
  const fallbackItems = useMemo(() => (markdown ? parseMdH2H3(markdown) : []), [markdown]);
  const items = domItems.length ? domItems : fallbackItems;

  // Track active section
  const [activeId, setActiveId] = useState("");
  useEffect(() => {
    if (!items.length) return;
    const targets = items.map((i) => document.getElementById(i.id)).filter(Boolean);
    if (!targets.length) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]?.target?.id) setActiveId(vis[0].target.id);
      },
      { rootMargin: "0px 0px -70% 0px", threshold: [0, 0.1, 1] }
    );
    targets.forEach((el) => obs.observe(el));
    return () => targets.forEach((el) => obs.unobserve(el));
  }, [items]);

  const scrollToId = (e, id) => {
    e?.preventDefault?.();
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
    window.scrollTo({ top, behavior: "smooth" });
    window.history?.replaceState?.(null, "", `#${id}`);
  };

  return (
    <>
      {/* -------- Mobile TOC (sticky, wrapping chips) -------- */}
      <MobileToc
        items={items.filter((i) => i.level === 2)} // H2 only on mobile for clarity
        activeId={activeId}
        onItemClick={scrollToId}
        title={title}
        shareUrl={shareUrl}
      />

      {/* -------- Desktop left rail (card) -------- */}
      <div className="hidden md:block">
        <div className="rounded-2xl border p-4">
          <div className="text-xs font-semibold tracking-wider text-gray-500 mb-3">
            TABLE OF CONTENTS
          </div>

          <div className="max-h-[calc(100vh-112px-160px)] overflow-auto pr-2 scroll-touch">
            <nav className="text-[15px] md:text-base lg:text-[17px] leading-6">
              {items.length ? (
                <ul className="space-y-2.5">
                  {items.map((it) => (
                    <li key={it.id}>
                      <a
                        href={`#${it.id}`}
                        onClick={(e) => scrollToId(e, it.id)}
                        className={[
                          "block transition-colors font-medium",
                          it.level === 3 ? "ml-3 text-gray-700" : "",
                          activeId === it.id
                            ? "text-orange-600 font-semibold"
                            : "text-gray-900 hover:text-orange-700",
                        ].join(" ")}
                      >
                        {it.text}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-gray-400">No sections found</div>
              )}
            </nav>
          </div>

          <div className="mt-4 pt-3 border-t">
            <ShareMenu title={title} shareUrl={shareUrl} />
          </div>
        </div>
      </div>
    </>
  );
}

/* ---------- Mobile TOC (sticky + wrapping chips + tiny share) ---------- */
function MobileToc({ items, activeId, onItemClick, title, shareUrl }) {
  if (!items?.length) return null;

  return (
    <div className="md:hidden sticky top-16 z-30 bg-white/90 backdrop-blur border-b">
      <div className="max-w-3xl mx-auto px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-semibold tracking-wider text-gray-500">
            ON THIS PAGE
          </div>
          <div className="-mr-1">
            <ShareMenuSmall title={title} shareUrl={shareUrl} />
          </div>
        </div>

        {/* Wrapping chips (no horizontal scroll) */}
        <nav className="mt-2 flex flex-wrap gap-2 pb-1">
          {items.map((it) => {
            const active = activeId === it.id;
            return (
              <a
                key={it.id}
                href={`#${it.id}`}
                onClick={(e) => onItemClick(e, it.id)}
                className={[
                  "rounded-full px-3 py-1.5 text-sm border",
                  "max-w-full truncate",
                  active
                    ? "bg-orange-600 text-white border-orange-600"
                    : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50",
                ].join(" ")}
                title={it.text}
              >
                {it.text}
              </a>
            );
          })}
        </nav>
      </div>
    </div>
  );
}

/* ---------- Desktop Share (portal popover) ---------- */
function ShareMenu({ title, shareUrl }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  // Respect shareUrl prop and convert to absolute
  const url = makeAbs(shareUrl) || (typeof window !== "undefined" ? window.location.href : "");
  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(title || "Check this out");

  const links = useMemo(
    () => [
      { name: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, icon: <FaLinkedin className="h-5 w-5" />, bg: "bg-[#0a66c2]", ring: "ring-[#0a66c2]/20" },
      { name: "X / Twitter", href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`, icon: <FaXTwitter className="h-5 w-5" />, bg: "bg-black", ring: "ring-black/20" },
      { name: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, icon: <FaFacebook className="h-5 w-5" />, bg: "bg-[#1877f2]", ring: "ring-[#1877f2]/20" },
      { name: "WhatsApp", href: `https://api.whatsapp.com/send?text=${encodedText}%20${encodedUrl}`, icon: <FaWhatsapp className="h-5 w-5" />, bg: "bg-[#25D366]", ring: "ring-[#25D366]/20" },
      { name: "Telegram", href: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`, icon: <FaTelegramPlane className="h-5 w-5" />, bg: "bg-[#229ED9]", ring: "ring-[#229ED9]/20" },
    ],
    [encodedUrl, encodedText]
  );

  const computePosition = () => {
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    setPos({ top: r.bottom + window.scrollY + 8, left: r.left + window.scrollX, width: r.width });
  };

  useLayoutEffect(() => {
    if (!open) return;
    computePosition();
    const onScroll = () => computePosition();
    const onResize = () => computePosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const pop = document.getElementById("share-popover");
      if (pop && !pop.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const openDirect = (href) => window.open(href, "_blank", "noopener,noreferrer");

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="w-full rounded-full border-2 border-purple-700 px-5 py-2.5 font-semibold text-purple-700 hover:bg-purple-50"
        aria-expanded={open}
        aria-haspopup="menu"
        type="button"
      >
        <span className="mr-2">🔗</span> Share
      </button>

      {open &&
        createPortal(
          <div
            id="share-popover"
            role="menu"
            style={{ position: "absolute", top: pos.top, left: pos.left, minWidth: pos.width }}
            className="z-[1000] rounded-xl border bg-white p-3 shadow-2xl"
          >
            <div style={{ position: "absolute", top: -6, left: 20 }} className="h-3 w-3 rotate-45 bg-white border-l border-t" />
            <div className="flex items-center gap-2">
              {links.map((l) => (
                <button
                  key={l.name}
                  onClick={() => openDirect(l.href)}
                  className={[
                    "inline-flex h-9 w-9 items-center justify-center rounded-full text-white",
                    l.bg,
                    "ring-2 hover:ring-4 transition-shadow",
                    l.ring,
                  ].join(" ")}
                  title={l.name}
                  aria-label={l.name}
                  type="button"
                >
                  {l.icon}
                </button>
              ))}
              <button
                onClick={async () => { try { await navigator.clipboard.writeText(url); } catch {} setOpen(false); }}
                className="ml-1 inline-flex h-9 px-3 items-center justify-center rounded-full bg-gray-900 text-white text-sm hover:ring-4 ring-gray-900/20"
                title="Copy link"
                aria-label="Copy link"
                type="button"
              >
                Copy
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

/* ---------- Tiny mobile share (inline) ---------- */
function ShareMenuSmall({ title, shareUrl }) {
  const [open, setOpen] = useState(false);

  // Respect shareUrl prop and convert to absolute
  const url = makeAbs(shareUrl) || (typeof window !== "undefined" ? window.location.href : "");
  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(title || "Check this out");

  // Prefer deep link on mobile-ish UAs
  const isLikelyMobile =
    typeof navigator !== "undefined" &&
    /Android|iPhone|iPad|iPod|Mobile|CriOS|FxiOS/i.test(navigator.userAgent);

  const waHref = isLikelyMobile
    ? `whatsapp://send?text=${encodedText}%20${encodedUrl}`
    : `https://api.whatsapp.com/send?text=${encodedText}%20${encodedUrl}`;

  const items = [
    { name: "WhatsApp",  href: waHref,                                               icon: <FaWhatsapp className="h-4 w-4" />, bg: "bg-[#25D366]" },
    { name: "LinkedIn",  href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`, icon: <FaLinkedin className="h-4 w-4" />, bg: "bg-[#0a66c2]" },
    { name: "X / Twitter", href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`, icon: <FaXTwitter className="h-4 w-4" />, bg: "bg-black" },
    { name: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,          icon: <FaFacebook className="h-4 w-4" />, bg: "bg-[#1877f2]" },
  ];

  const openDirect = (href) => { window.open(href, "_blank", "noopener,noreferrer"); setOpen(false); };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="rounded-full border px-3 py-1.5 text-xs font-semibold text-purple-700 border-purple-700 hover:bg-purple-50"
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        Share
      </button>
      {open && (
        <div role="menu" className="absolute right-0 mt-2 rounded-xl border bg-white p-2 shadow-2xl">
          <div className="flex items-center gap-2">
            {items.map((i) => (
              <button
                key={i.name}
                onClick={() => openDirect(i.href)}
                className={[
                  "inline-flex h-8 w-8 items-center justify-center rounded-full text-white",
                  i.bg,
                ].join(" ")}
                aria-label={i.name}
                title={i.name}
                type="button"
              >
                {i.icon}
              </button>
            ))}
            <button
              onClick={async () => {
                try { await navigator.clipboard.writeText(url); } catch {}
                setOpen(false);
              }}
              className="inline-flex h-8 px-2 items-center justify-center rounded-full bg-gray-900 text-white text-[11px]"
              aria-label="Copy"
              title="Copy"
              type="button"
            >
              Copy
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
